import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { RootState, StateFromRoot, registerReducer, store } from "@core/store"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { OSINT } from "../interfaces"

const STATE_KEY = "osint_sherlok"
export const CONFIG_KEY = `${STATE_KEY}_state`

export interface SherlokFilters {
  status?: OSINT.Status
  type?: OSINT.PayloadType
}

interface IState {
  filters: SherlokFilters
  pageSize: number
  // Стек предыдущих курсоров для prev-навигации.
  // cursorStack[i] — курсор, который надо передать, чтобы получить страницу i.
  // cursorStack[0] всегда null (первая страница).
  cursorStack: (string | null)[]
  pageIndex: number
  autoRefresh: boolean
}

// В localStorage сохраняем только пользовательские предпочтения,
// рантайм-состояние (cursorStack, pageIndex) — нет.
interface IPersistedState {
  filters: SherlokFilters
  pageSize: number
  autoRefresh: boolean
}

const persistantRaw = localStorage.getItem(CONFIG_KEY)
const persisted: Partial<IPersistedState> = persistantRaw
  ? JSON.parse(persistantRaw)
  : {}

const initialState: IState = {
  filters: persisted.filters ?? {},
  pageSize: persisted.pageSize ?? GRID_PAGE_SIZE_OPTIONS[0],
  autoRefresh: persisted.autoRefresh ?? true,
  cursorStack: [null],
  pageIndex: 0,
}

export const sherlokSlice = createSlice({
  name: "sherlok",
  initialState,
  reducers: {
    setFilters: (state, { payload }: PayloadAction<SherlokFilters>) => {
      state.filters = payload
      // При смене фильтров сбрасываем пагинацию.
      state.cursorStack = [null]
      state.pageIndex = 0
    },
    setPageSize: (state, { payload }: PayloadAction<number>) => {
      state.pageSize = payload
      state.cursorStack = [null]
      state.pageIndex = 0
    },
    setAutoRefresh: (state, { payload }: PayloadAction<boolean>) => {
      state.autoRefresh = payload
    },
    pushCursor: (state, { payload }: PayloadAction<string | null>) => {
      // payload — следующий курсор (nextCursor из предыдущего ответа).
      state.cursorStack.push(payload)
      state.pageIndex = state.cursorStack.length - 1
    },
    popCursor: (state) => {
      if (state.cursorStack.length > 1) {
        state.cursorStack.pop()
        state.pageIndex = state.cursorStack.length - 1
      }
    },
    resetPagination: (state) => {
      state.cursorStack = [null]
      state.pageIndex = 0
    },
  },
})

registerReducer(sherlokSlice.name, sherlokSlice.reducer)

store.subscribe(() => {
  const s = (store.getState() as any).sherlok as IState | undefined
  if (!s) return
  const persistable: IPersistedState = {
    filters: s.filters,
    pageSize: s.pageSize,
    autoRefresh: s.autoRefresh,
  }
  localStorage.setItem(CONFIG_KEY, JSON.stringify(persistable))
})

export const {
  setFilters,
  setPageSize,
  setAutoRefresh,
  pushCursor,
  popCursor,
  resetPagination,
} = sherlokSlice.actions

export const getSherlokState = (state: RootState<StateFromRoot<IState>>) =>
  state.sherlok as IState
